import type { JSX } from "react";
import { cn } from "./cn";
import { NavArrows } from "./NavArrows";
import { CalendarEvent } from "./CalendarEvent";

export interface WeekDay {
  readonly day: string;
  readonly date: string;
  readonly highlight?: boolean;
}

export interface WeekEvent {
  readonly title: string;
  readonly time?: string;
  readonly color: string;
  readonly column?: number;
  readonly row?: number | string;
}

export interface WeekCalendarProps {
  /** Date range label shown in navigation */
  readonly dateRange: string;
  /** Day column headers */
  readonly days: readonly WeekDay[];
  /** Events to render in the grid */
  readonly events: readonly WeekEvent[];
  /** Left-side time labels */
  readonly timeLabels: readonly string[];
  /** Hide the built-in NavArrows (when external navigation is provided) */
  readonly hideNav?: boolean;
  /** Extra className */
  readonly className?: string;
}

/**
 * A week-view calendar with day columns, time rows, and event blocks.
 */
export function WeekCalendar({
  dateRange,
  days,
  events,
  timeLabels,
  hideNav,
  className,
}: WeekCalendarProps): JSX.Element {
  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {!hideNav && <NavArrows label={dateRange} />}

      <div className="flex gap-0">
        {/* Time column */}
        <div className="flex flex-col gap-4 pt-10 pr-2">
          {timeLabels.map((label) => (
            <span key={label} className="text-[10px] text-content-tertiary h-10 flex items-center">
              {label}
            </span>
          ))}
        </div>

        {/* Day columns */}
        <div className="grid flex-1" style={{ gridTemplateColumns: `repeat(${String(days.length)}, 1fr)` }}>
          {days.map((entry) => (
            <div key={entry.day} className="flex flex-col items-center border-l border-edge">
              <div
                className={cn(
                  "flex flex-col items-center py-2 text-xs gap-0.5",
                  entry.highlight ? "text-accent font-semibold" : "text-content-tertiary",
                )}
              >
                <span>{entry.day}</span>
                <strong className="text-sm">{entry.date}</strong>
              </div>
            </div>
          ))}

          {events.map((event) => (
            <CalendarEvent
              key={`${event.title}-${String(event.column)}-${String(event.row)}`}
              title={event.title}
              time={event.time}
              color={event.color}
              size="sm"
              style={{ gridColumn: event.column, gridRow: event.row }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
